import { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DisabledByDefaultIcon from "@mui/icons-material/DisabledByDefault";
import { IFlightType } from "@vmosolutions/oc-interfaces";

/** Tri-state cycle: each click advances to the next entry, wrapping at the end. */
const STATES = ["any", "include", "exclude"] as const;
type TriState = (typeof STATES)[number];
const nextState = (s: TriState): TriState => STATES[(STATES.indexOf(s) + 1) % STATES.length];

const stateIcon = (state: TriState): JSX.Element => {
  if (state === "include") return <CheckBoxIcon color="success" fontSize="small" />;
  if (state === "exclude") return <DisabledByDefaultIcon color="error" fontSize="small" />;
  return <CheckBoxOutlineBlankIcon color="disabled" fontSize="small" />;
};

interface IFlightTypeTriStateFieldProps {
  flightTypes: IFlightType[];
  includedIds: string[] | undefined;
  excludedIds: string[] | undefined;
  onChange: (included: string[], excluded: string[]) => void;
  /** Compact variant fits inside a DataGrid cell; full variant is for the AddRule dialog. */
  variant?: "compact" | "full";
  disabled?: boolean;
}

/**
 * Render the human-readable summary of an include/exclude pair. Same logic used by
 * both the cell display and the popover trigger.
 */
const summarize = (
  flightTypes: IFlightType[],
  includedIds: string[] | undefined,
  excludedIds: string[] | undefined
): string => {
  const nameById = new Map(flightTypes.map((ft) => [String(ft._id), ft.title ?? "(unnamed)"]));
  const inc = (includedIds ?? []).map((id) => nameById.get(id) ?? id);
  const exc = (excludedIds ?? []).map((id) => nameById.get(id) ?? id);
  if (inc.length === 0 && exc.length === 0) return "Any";
  if (exc.length === 0) return inc.join(", ");
  if (inc.length === 0) return `Except ${exc.join(", ")}`;
  return `${inc.join(", ")} [Except ${exc.join(", ")}]`;
};

/**
 * Tri-state Flight Type selector. Each flight type has three states - blank (no
 * constraint), included, or excluded - clicking the icon cycles via `nextState`. Two
 * arrays come back via `onChange`: the include set and the exclude set. Both empty =
 * "any flight type".
 *
 * Rendered as a button that pops open a list of all flight types; click each row's
 * icon to cycle. Bypasses MUI DataGrid's single-input edit mode (used in `renderCell`
 * rather than `renderEditCell`) because a single cell needs to express two arrays.
 */
export const FlightTypeTriStateField = (props: IFlightTypeTriStateFieldProps): JSX.Element => {
  const { flightTypes, includedIds, excludedIds, onChange, variant = "compact", disabled = false } = props;
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);

  const sortedTypes = useMemo(
    () => [...flightTypes].sort((a, b) => (a.title ?? "").localeCompare(b.title ?? "")),
    [flightTypes]
  );

  const stateOf = (id: string): TriState => {
    if ((includedIds ?? []).includes(id)) return "include";
    if ((excludedIds ?? []).includes(id)) return "exclude";
    return "any";
  };

  const summary = summarize(flightTypes, includedIds, excludedIds);

  const handleCycle = (id: string): void => {
    const next = nextState(stateOf(id));
    const nextInc = (includedIds ?? []).filter((x) => x !== id);
    const nextExc = (excludedIds ?? []).filter((x) => x !== id);
    if (next === "include") nextInc.push(id);
    if (next === "exclude") nextExc.push(id);
    onChange(nextInc, nextExc);
  };

  const handleReset = (): void => onChange([], []);

  return (
    <>
      <Tooltip title={summary === "Any" ? "Any flight type (click to constrain)" : summary}>
        <Button
          ref={anchorRef}
          variant={variant === "compact" ? "text" : "outlined"}
          size="small"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          sx={{
            justifyContent: variant === "compact" ? "center" : "flex-start",
            textTransform: "none",
            minWidth: 0,
            width: "100%",
            color: summary === "Any" ? "text.secondary" : "text.primary",
            fontStyle: summary === "Any" ? "italic" : "normal",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {summary}
        </Button>
      </Tooltip>
      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 1500 }}>
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper sx={{ minWidth: 260, maxHeight: 360, overflow: "auto" }} elevation={6}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Click an icon to cycle: blank -&gt; include -&gt; exclude
              </Typography>
              <Button size="small" onClick={handleReset} disabled={summary === "Any"}>
                Reset
              </Button>
            </Box>
            <List dense disablePadding>
              {sortedTypes.length === 0 && (
                <ListItem>
                  <ListItemText primary="No flight types defined" />
                </ListItem>
              )}
              {sortedTypes.map((ft) => {
                const id = String(ft._id);
                const state = stateOf(id);
                return (
                  <ListItemButton key={id} onClick={() => handleCycle(id)}>
                    <IconButton
                      size="small"
                      edge="start"
                      sx={{ mr: 1 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCycle(id);
                      }}
                    >
                      {stateIcon(state)}
                    </IconButton>
                    <ListItemText
                      primary={ft.title ?? "(unnamed)"}
                      secondary={ft.description}
                      primaryTypographyProps={{ noWrap: true }}
                      secondaryTypographyProps={{ noWrap: true }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

/** Read-only summary helper exported for the grid's non-editing cell render. */
export const flightTypeFilterSummary = summarize;

export default FlightTypeTriStateField;
